import { useRef, useState, useEffect } from "react";
import { comparisonItems, rankingCompanies } from "@/mocks/rankingData";

type RatingKey = "aRating" | "bRating" | "cRating" | "dRating" | "eRating";
type ValueKey = "a" | "b" | "c" | "d" | "e";

const ratingClass = (rating: string) => {
  if (rating === "◎") return "text-brand-600 font-bold";
  if (rating === "◯") return "text-emerald-500 font-bold";
  if (rating === "△") return "text-amber-500 font-bold";
  if (rating === "✕") return "text-red-400 font-bold";
  return "text-gray-400";
};

export default function ComparisonTable() {
  const ratingKeys: RatingKey[] = ["aRating", "bRating", "cRating", "dRating", "eRating"];
  const valueKeys: ValueKey[] = ["a", "b", "c", "d", "e"];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    };
    check();
    el.addEventListener("scroll", check);
    window.addEventListener("resize", check);
    return () => {
      el.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  return (
    <section id="comparison" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto md:px-6">
        {/* Header */}
        <div className="text-center mb-12 px-6 md:px-0">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            5社まとめてチェック！
            <br className="md:hidden" />
            比較表
          </h2>
        </div>

        {/* Mobile scroll hint */}
        <div className="md:hidden flex items-center justify-end gap-1 px-4 mb-2">
          <span className="text-xs text-gray-400">スワイプして全社を確認</span>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs text-gray-400"></i>
          </div>
        </div>

        {/* Scroll container: mobile = flush to screen edges, desktop = contained */}
        <div className="relative">
          <div
            ref={scrollRef}
            className="overflow-x-auto md:rounded-xl md:border md:border-gray-200"
            style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
          >
            <table className="w-full min-w-[640px] md:min-w-[820px] border-collapse">
              <thead>
                <tr className="bg-brand-800 text-white">
                  <th className="py-4 px-5 text-left text-sm font-semibold w-32 md:w-36">項目</th>
                  {rankingCompanies.map((c) => (
                    <th key={c.rank} className="py-4 px-2 md:px-3 text-center text-sm font-semibold">
                      <div className="flex flex-col items-center gap-1">
                        <span
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                          style={{ backgroundColor: c.color }}
                        >
                          {c.rank}
                        </span>
                        <span className="text-[11px] leading-tight">{c.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonItems.map((item, idx) => (
                  <tr
                    key={idx}
                    className={`border-b border-gray-100 ${idx % 2 === 0 ? "bg-white" : "bg-brand-50/30"}`}
                  >
                    <td className="py-3 px-4 md:px-5 text-sm font-semibold text-gray-700 bg-brand-50/50 border-r border-gray-100 whitespace-nowrap">
                      {item.label}
                    </td>
                    {valueKeys.map((vk, ki) => {
                      const rk = ratingKeys[ki];
                      return (
                        <td key={vk} className="py-3 px-2 md:px-3 text-center align-middle">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className={`text-base ${ratingClass(item[rk])}`}>{item[rk]}</span>
                            <span className="text-gray-600 text-[10px] md:text-[11px] leading-snug max-w-[80px] md:max-w-[100px]">
                              {item[vk]}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Right fade gradient — mobile only, shown when scrollable */}
          {canScrollRight && (
            <div className="md:hidden pointer-events-none absolute top-0 right-0 h-full w-10 bg-gradient-to-l from-white/80 to-transparent" />
          )}
        </div>
      </div>
    </section>
  );
}